import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import axios from 'axios';
import { useMsal } from "@azure/msal-react";
import { loginRequest } from "../authConfig";
import { jwtDecode } from 'jwt-decode';

const API_BASE_URL = process.env.REACT_APP_API_URL || '';

// Check if JWT token is expired (with 30 second buffer)
const isTokenExpired = (token) => {
  if (!token) return true;
  try {
    const decoded = jwtDecode(token);
    if (decoded.exp) {
      return decoded.exp * 1000 < Date.now() + 30000;
    }
    return false;
  } catch (error) {
    console.error('Error decoding token:', error);
    return true;
  }
};

// Check if token expires within the given window (ms). Used to refresh before expiry when user returns to tab.
const isTokenExpiringWithin = (token, withinMs = 5 * 60 * 1000) => {
  if (!token) return true;
  try {
    const decoded = jwtDecode(token);
    if (decoded.exp) {
      return decoded.exp * 1000 < Date.now() + withinMs;
    }
    return false;
  } catch (error) {
    return true;
  }
};

export const useAuth = () => {
  const { instance } = useMsal();
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('auth_token'));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // Initialize isAuthenticated based on token existence
  const [isAuthenticated, setIsAuthenticated] = useState(!!localStorage.getItem('auth_token'));

  // Track if we just logged in (to prevent useEffect from interfering)
  const justLoggedIn = useRef(false);
  
  // Track refresh attempts to prevent infinite loops
  const refreshAttempts = useRef(0);
  const MAX_REFRESH_ATTEMPTS = 1;

  // Create axios instance with auth token - memoized to prevent recreation
  // Timeout 60s to avoid "timeout of 30000 ms exceeded" when window was left open (cold start / slow first request)
  const apiClient = useMemo(() => {
    const client = axios.create({
      baseURL: API_BASE_URL,
      timeout: 60000,
    });

    // Add token to requests; 401 handling and refresh are managed by response interceptor.
    client.interceptors.request.use(async (config) => {
      const currentToken = localStorage.getItem('auth_token');
      if (currentToken) {
        config.headers.Authorization = `Bearer ${currentToken}`;
      }
      return config;
    });

    return client;
  }, []);

  // Clear local auth state only (no redirect to Microsoft). Use when refresh fails so user can log in again without seeing "You signed out" page.
  const clearAuthState = useCallback(() => {
    localStorage.removeItem('auth_token');
    setToken(null);
    setUser(null);
    setIsAuthenticated(false);
  }, []);

  // Handle silent token refresh with retry limit
  const refreshToken = useCallback(async (options = {}) => {
    const forceRefresh = options?.forceRefresh === true;
    // Check if we've exceeded max refresh attempts
    if (refreshAttempts.current >= MAX_REFRESH_ATTEMPTS) {
      console.error('Max refresh attempts exceeded');
      refreshAttempts.current = 0;
      clearAuthState(); // Don't redirect to Microsoft sign-out; just clear state so user can log in again
      throw new Error('Max refresh attempts exceeded');
    }
    
    refreshAttempts.current++;
    
    try {
      const currentAccounts = instance.getAllAccounts();
      if (currentAccounts.length === 0) {
        throw new Error('No accounts found for refresh');
      }

      const tokenResponse = await instance.acquireTokenSilent({
        ...loginRequest,
        account: currentAccounts[0],
        forceRefresh, // Use forced refresh only when explicitly requested
      });

      const newIdToken = tokenResponse.idToken;
      localStorage.setItem('auth_token', newIdToken);
      setToken(newIdToken);
      setIsAuthenticated(true);
      
      // Reset attempts on success
      refreshAttempts.current = 0;
      
      return newIdToken;
    } catch (err) {
      console.error('Failed to refresh token silently:', err);
      // Silent refresh failed (expired session, etc.). Clear local state only so user can sign in again
      // without being sent to Microsoft "You signed out" page.
      if (err.name === 'InteractionRequiredAuthError') {
        refreshAttempts.current = 0;
        clearAuthState();
      }
      throw err;
    }
  }, [instance, clearAuthState]);

  // Handle token expiration (only for actual API calls, not during login)
  useEffect(() => {
    const responseInterceptor = apiClient.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;
        const requestUrl = String(originalRequest?.url || '');
        const hasLocalToken = Boolean(localStorage.getItem('auth_token'));

        // Handle 401 and avoid infinite loops.
        // Do not gate this on justLoggedIn; otherwise post-login sessions can miss refresh.
        if (
          error.response?.status === 401 &&
          !originalRequest?._retry &&
          hasLocalToken &&
          !requestUrl.includes('/auth/validate-token')
        ) {
          originalRequest._retry = true;

          try {
            console.log('401 detected, attempting silent token refresh...');
            const newToken = await refreshToken({ forceRefresh: false });

            // Update the Authorization header and retry
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            return apiClient(originalRequest);
          } catch (refreshError) {
            console.error('Token refresh failed, clearing auth state:', refreshError);
            clearAuthState();
            // Reject with a clear message so UI shows session expired (whether caller uses error.message or response.data.detail)
            const friendlyMessage = 'Your session has expired. Please sign in again.';
            const sessionError = new Error(friendlyMessage);
            sessionError.response = error.response
              ? { ...error.response, data: { ...error.response.data, detail: friendlyMessage } }
              : undefined;
            return Promise.reject(sessionError);
          }
        }
        return Promise.reject(error);
      }
    );

    return () => {
      apiClient.interceptors.response.eject(responseInterceptor);
    };
  }, [apiClient, refreshToken, clearAuthState]);

  // Proactive token refresh when user returns to tab (after leaving window open)
  // Reduces "session expired" or 401 when using the tool after long idle
  useEffect(() => {
    const refreshIfNeeded = async () => {
      const storedToken = localStorage.getItem('auth_token');
      if (!storedToken || !isAuthenticated) return;
      if (!isTokenExpired(storedToken) && !isTokenExpiringWithin(storedToken)) return;
      try {
        console.log('Token expired or expiring soon, refreshing on tab focus...');
        const newToken = await refreshToken({ forceRefresh: true });
        if (newToken) {
          setToken(newToken);
        }
      } catch (err) {
        console.warn('Proactive token refresh failed:', err);
        clearAuthState();
      }
    };

    const handleVisibility = () => {
      if (document.visibilityState === 'visible') refreshIfNeeded();
    };
    const handleFocus = () => refreshIfNeeded();

    document.addEventListener('visibilitychange', handleVisibility);
    window.addEventListener('focus', handleFocus);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibility);
      window.removeEventListener('focus', handleFocus);
    };
  }, [isAuthenticated, refreshToken, clearAuthState]);

  // Multi-tab logout sync - listen for storage changes
  useEffect(() => {
    const handleStorageChange = (e) => {
      // If auth_token was removed in another tab
      if (e.key === 'auth_token' && !e.newValue && isAuthenticated) {
        console.log('Token removed in another tab, logging out...');
        setUser(null);
        setToken(null);
        setIsAuthenticated(false);
      }
      // If auth_token was added in another tab
      else if (e.key === 'auth_token' && e.newValue && !isAuthenticated) {
        console.log('Token added in another tab, updating auth state...');
        setToken(e.newValue);
        setIsAuthenticated(true);
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [isAuthenticated]);

  // Load user from token on mount ONLY (not on token changes from login)
  useEffect(() => {
    const loadUser = async () => {
      // If we just logged in, skip this effect - user is already set
      if (justLoggedIn.current) {
        justLoggedIn.current = false;
        return;
      }

      const storedToken = localStorage.getItem('auth_token');

      if (!storedToken) {
        setIsAuthenticated(false);
        setUser(null);
        return;
      }
      
      // Check if token is expired
      if (isTokenExpired(storedToken)) {
        console.log('Stored token is expired, clearing auth state...');
        localStorage.removeItem('auth_token');
        setIsAuthenticated(false);
        setUser(null);
        setToken(null);
        return;
      }

      // Token exists and is valid - set authenticated immediately
      setIsAuthenticated(true);
      setToken(storedToken);

      // If user is already set, don't reload
      if (user) {
        return;
      }

      // Try to load user info from API
      try {
        const response = await apiClient.get('/auth/me');
        setUser(response.data);
      } catch (err) {
        console.error('Failed to load user:', err);
        // If 401 happens here, the interceptor above will handle it
      }
    };

    loadUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run only on mount

  const loginWithAzure = useCallback(async () => {
    setLoading(true);
    setError(null);
    justLoggedIn.current = true;

    try {
      // Use redirect instead of popup to avoid nested popup errors
      // The response will be handled in index.js via handleRedirectPromise
      // and then picked up here via MSAL's account state
      await instance.loginRedirect(loginRequest);
      // Note: This line won't be reached because the page navigates away.
      // The actual login completion is handled when the user returns.
    } catch (err) {
      console.error('Azure login error:', err);
      setError(err.message || "Azure login failed");
      justLoggedIn.current = false;
      setLoading(false);
      return { success: false, error: err.message };
    }
  }, [instance]);

  // Handle Azure AD redirect response when user returns
  // Use MSAL event system for more reliable detection
  useEffect(() => {
    let isMounted = true;
    let msalCallbackId = null;

    const handleRedirectResponse = async () => {
      if (!isMounted) return;
      // Check if we have an account after redirect
      const currentAccounts = instance.getAllAccounts();
      console.log('Checking MSAL accounts:', currentAccounts.length, 'isAuthenticated:', isAuthenticated);

      if (currentAccounts.length > 0 && !isAuthenticated) {
        const account = currentAccounts[0];
        instance.setActiveAccount(account);
        console.log('Found MSAL account, processing login for:', account.username);

        try {
          setLoading(true);
          // Silently acquire token to validate and get ID token
          const tokenResponse = await instance.acquireTokenSilent({
            ...loginRequest,
            account: account,
          });

          const idToken = tokenResponse.idToken;
          console.log('Acquired token silently, validating with backend...');

          // Validate with backend (token in body to avoid leaking in URL/logs)
          const response = await apiClient.post('/auth/validate-token', { token: idToken });

          if (response.data.valid) {
            const { user: userData } = response.data;
            localStorage.setItem('auth_token', idToken);
            setToken(idToken);
            setUser(userData);
            setIsAuthenticated(true);
            justLoggedIn.current = true;
            console.log('Azure AD login successful via redirect:', userData.email);
          } else {
            console.error('Backend validation returned invalid');
            setError('Backend validation failed');
          }
        } catch (err) {
          console.error('Failed to process Azure redirect login:', err);
          // If silent token acquisition fails, it might need interaction
          if (err.name === 'InteractionRequiredAuthError') {
            console.log('Silent token acquisition failed, user may need to re-login');
          }
          setError(err.message || 'Failed to complete Azure login');
        } finally {
          setLoading(false);
        }
      }
    };

    // Run immediately on mount AND when accounts might have changed
    handleRedirectResponse();

    // Also set up an event listener for when MSAL finishes processing redirects
    import("@azure/msal-browser").then(({ EventType }) => {
      if (!isMounted) return;
      msalCallbackId = instance.addEventCallback((event) => {
        if (event.eventType === EventType.LOGIN_SUCCESS || event.eventType === EventType.ACQUIRE_TOKEN_SUCCESS) {
          console.log('MSAL login/token event detected:', event.eventType);
          handleRedirectResponse();
        }
      });
    });

    return () => {
      isMounted = false;
      if (msalCallbackId) {
        instance.removeEventCallback(msalCallbackId);
      }
    };
  }, [instance, apiClient, isAuthenticated]);


  /**
   * Log out. By default redirects to Microsoft sign-out (so user sees "You signed out" then returns to app).
   * Pass { skipMicrosoftLogout: true } to only clear local state (e.g. after failed refresh) so user can sign in again without the Microsoft sign-out page.
   */
  const logout = useCallback((options) => {
    const skipMicrosoftLogout = options?.skipMicrosoftLogout === true;

    localStorage.removeItem('auth_token');
    setToken(null);
    setUser(null);
    setIsAuthenticated(false);

    if (!skipMicrosoftLogout) {
      instance.logoutRedirect({
        postLogoutRedirectUri: window.location.origin,
      });
    }
  }, [instance]);

  const updateProfile = useCallback(async (profileData) => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiClient.put('/auth/me', profileData);
      setUser(response.data);
      return { success: true, user: response.data };
    } catch (err) {
      const errorMessage = err.response?.data?.detail || err.message || 'Failed to update profile';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  }, [apiClient]);

  return {
    user,
    token,
    loading,
    error,
    loginWithAzure,
    logout,
    updateProfile,
    isAuthenticated,
    apiClient, // Export the authenticated client
    refreshToken, // Export the refresh function for manual/streaming use
  };
};
